import {
  HStack,
  isCompound,
  PopoverContent,
  Text,
  VStack,
  type BlotterTableContextProps,
  type Column,
  type Security,
} from '@talos/kyoko';
import type { ITooltipParams } from 'ag-grid-community';

interface CompoundSecurityLegTooltipItem {
  displaySymbol: string;
  sizeRatio?: string;
}

/** Attach this onto a type: "security" column to show Compound Instrument leg symbol size ratios to the user */
export const compoundSecurityLegsTooltipColumnProps = {
  tooltipValueGetter: getCompoundSecurityLegTooltipValue,
  tooltipComponent: CompoundSecurityLegsTooltip,
} satisfies Pick<Column, 'tooltipValueGetter' | 'tooltipComponent'>;

function getCompoundSecurityLegTooltipValue({ context, value }: ITooltipParams<unknown, unknown>) {
  const symbol = typeof value === 'string' ? value : undefined; // we just handle strings. I think the column can also have value be an array of strings, but we dont care
  if (!symbol) {
    return null;
  }

  const securitiesBySymbol = context?.current?.securitiesBySymbol;
  const security = securitiesBySymbol?.get(symbol);
  if (!security || !isCompound(security) || !security.Legs?.length) {
    return null;
  }

  return security.Legs.map(leg => getCompoundSecurityLegTooltipItem(leg, securitiesBySymbol));
}

function CompoundSecurityLegsTooltip({ value }: ITooltipParams<unknown, CompoundSecurityLegTooltipItem[]>) {
  if (!value?.length) {
    return null;
  }

  return (
    <PopoverContent>
      <VStack w="200px" gap="spacing4" alignItems="stretch">
        <HStack color="colorTextDefault" fontWeight="fontWeightBold" justifyContent="space-between">
          <Text>Leg Symbol</Text>
          <Text>Size Ratio</Text>
        </HStack>
        {value.map(({ displaySymbol, sizeRatio }) => (
          <HStack key={`${displaySymbol}-${sizeRatio}`} justifyContent="space-between" gap="spacing8">
            <Text color="colorTextDefault">{displaySymbol}</Text>
            <Text color="colorTextImportant">{sizeRatio}</Text>
          </HStack>
        ))}
      </VStack>
    </PopoverContent>
  );
}

function getCompoundSecurityLegTooltipItem(
  leg: NonNullable<Security['Legs']>[number],
  securitiesBySymbol: BlotterTableContextProps['securitiesBySymbol']
): CompoundSecurityLegTooltipItem {
  const legSecurity = securitiesBySymbol?.get(leg.Symbol);
  return {
    displaySymbol: legSecurity?.DisplaySymbol || leg.Symbol,
    sizeRatio: leg.SizeRatio,
  };
}
